import { normalizeAvatar } from 'tools/Files/normalize'
import { toDictOn } from 'utils/array'
import { freeze, shallowFreeze } from 'utils/dom'
import { fromNowWithDate } from 'utils/time'

import { normalizeProject } from 'libreon/Project/normalize'
import { pathTo } from 'libreon/routes'

// who is target audience
// - readers digest version -

export const DEFAULT_STATE = shallowFreeze({
  // userId: undefined,
  // privs: {},
  routeId: undefined,
  discussId: undefined,
  originId: undefined,
  perms: {},
  type: undefined,
  linkTo: '',
  posts: [],
  post: {},
  comments: []
})

function normalizeAuthor(author) {
  if (!author) return undefined
  return freeze({
    ...author,
    avatar: normalizeAvatar(author.avatar?.path, author.id),
    linkTo: pathTo('user', { userId: author.handle })
  })
}

function getDateAgo(t) {
  const { ago, date } = fromNowWithDate(t)
  if (date) {
    return (
      <>
        {ago} ago <span className="f1 minimize">({date})</span>
      </>
    )
  } else {
    return <>{ago} ago</>
  }
}

function normalizeReply(state, post, msg) {
  return shallowFreeze({
    ...msg,
    author: normalizeAuthor(msg.author),
    perms: {
      canEdit: state.enabled && msg.author.id === state.userId
    },
    ago: getDateAgo(msg.insertedAt)
  })
}

////////////////////////////////////////////////////////////////////////////////
function normalizePerms(type, origin, discuss) {
  switch (type) {
    case DISCUSS_TYPES.PROJ_DISCUSS:
      return normalizeProjectStandardPerms(origin)
    case DISCUSS_TYPES.PROJ_NEWS:
      return normalizeProjectNewsPerms(origin)
    case DISCUSS_TYPES.PROJ_CLUB:
      return normalizeProjectClubPerms(origin)
    default:
      alert(`unsupported perm model: ${type.description}`)
  }
}

function normalizeProjectStandardPerms(project) {
  const { features, canEdit } = project
  const enabled = !!features.discuss
  const isAdmin = canEdit === true
  return {
    enabled,
    perms: {
      isAdmin,
      canPost: enabled && (isAdmin || !features.stdAdmPost),
      canReply: enabled && (isAdmin || !features.stdAdmReply)
    }
  }
}

function normalizeProjectClubPerms(project) {
  const { features, canEdit } = project
  const enabled = !!features.bookclub
  const isAdmin = canEdit === true
  return { enabled, perms: { isAdmin, canPost: enabled, canReply: enabled } }
}

function normalizeProjectNewsPerms(project) {
  const { features, canEdit } = project
  const enabled = !!features.news
  const isAdmin = canEdit === true
  return {
    enabled,
    perms: {
      isAdmin,
      canPost: enabled && isAdmin,
      canReply: enabled && (isAdmin || !features.newsAdmReply)
    }
  }
}

////////////////////////////////////////////////////////////////////////////////
export const DISCUSS_TYPES = {
  PROJ_DISCUSS: Symbol('project_discuss'),
  PROJ_CLUB: Symbol('project_bookclub'),
  PROJ_NEWS: Symbol('project_news')
  // USER_STUFF here
}

export const DISCUSS_META = {
  [DISCUSS_TYPES.PROJ_DISCUSS]: {
    label: 'Discussions',
    isAre: 'are',
    origin: 'title' // probably should xref global for this but ohwell
  },
  [DISCUSS_TYPES.PROJ_CLUB]: {
    label: 'Book Club',
    isAre: 'is',
    origin: 'title'
  },
  [DISCUSS_TYPES.PROJ_NEWS]: {
    label: 'Updates',
    isAre: 'are',
    origin: 'title'
  }
}

export const ROUTE_TO_TYPE = {
  project_standard: DISCUSS_TYPES.PROJ_DISCUSS,
  project_discuss: DISCUSS_TYPES.PROJ_DISCUSS,
  project_bookclub: DISCUSS_TYPES.PROJ_CLUB,
  project_news: DISCUSS_TYPES.PROJ_NEWS
  // user_stuff here
}

export function routeToType(type) {
  const result = ROUTE_TO_TYPE[type]
  if (!result) console.error('unrecognized Discussion type: ' + type)
  return result
}

export function normalizeOrigin({
  origin,
  type,
  userId,
  discussId,
  discussShortId
}) {
  return freeze({
    userId,
    discussId,
    type,
    originId: origin.id,
    ...normalizePerms(type, origin),
    avatar: normalizeAvatar(origin.avatar?.path, origin.id),
    linkTo: pathTo(type.description, {
      originId: origin.shortId,
      projectId: origin.shortId,
      discussId: discussShortId || discussId,
      userId
    })
  })
}

export function normalizePostList(state, { pd, type }) {
  const discuss = pd.discuss
  if (!discuss) return state

  state = { ...state, discuss: discuss, posts: [] }
  if (discuss.posts)
    state.posts = freeze(discuss.posts.map((t) => normalizePost(state, t)))

  return freeze(state)
}

export function normalizePost(state, post) {
  post = {
    ...post,
    ago: getDateAgo(post.insertedAt),
    perms: {
      canEdit: state.enabled && post.author.id === state.userId,
      isAdmin: state.perms.isAdmin
    },
    author: normalizeAuthor(post.author),
    recipient: normalizeAuthor(post.recipient),
    linkTo: `${state.linkTo}/${post.ident}`,
    className: post.visible ? '' : 'strike'
  }

  if (post.replies) {
    const list = freeze(
      post.replies.results.map((m) => normalizeReply(state, post, m))
    )
    const tree = post.replies.meta?.tree

    post.replies = { map: toDictOn(list, 'id'), list, tree }
  }

  // TODO: need to refactor the walk-up use case
  if (post.discuss?.project) {
    post.discuss = freeze({
      ...post.discuss,
      project: normalizeProject(post.discuss.project)
    })
  }

  return shallowFreeze(post)
}
